import {Component, OnInit, OnDestroy} from '@angular/core';
import {Router, ActivatedRoute} from "@angular/router";
import { PaymentService } from 'src/services/payment.service';
import { RouteHelperService } from 'src/services/route-helper.service';

@Component({
  selector: 'app-failure',
  templateUrl: './failure.component.html',
  styleUrls: ['./failure.component.scss']
})
export class FailureComponent implements OnInit, OnDestroy {
  countdown: number = 15;
  transactionDetails: any = {
    checkoutId: null,
    reference: 'N/A',
    error: 'Payment failed',
    date: new Date(),
    status: 'Failed',
    plateNumber: null,
    zoneName: null,
    // Bulk payment support
    plateNumbers: [],
    zoneNames: [],
    sessionCount: 1,
    isBulkPayment: false
  };
  isVerifying: boolean = true;
  verificationError: string = '';
  private countdownInterval: any;

  constructor(
    private router: Router, 
    private route: ActivatedRoute,
    private paymentService: PaymentService,
    public routeHelper: RouteHelperService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(async params => {
      
      // Extract parameters using multiple possible names (similar to success component)
      const checkoutId = this.extractCheckoutId(params);
      const reference = params['reference'] || params['referenceNumber'] || params['ref'];
      const errorMessage = params['error'] || params['errorMessage'] || params['message'] || 'Payment failed';
      const amount = params['amount'] || params['totalAmount'] || params['value'];

      this.transactionDetails = {
        checkoutId: checkoutId,
        reference: reference || 'N/A',
        error: errorMessage,
        amount: amount || 'N/A',
        date: new Date(),
        status: 'Failed',
        plateNumber: params['plateNumber'] || null,
        zoneName: params['zoneName'] || null
      };

      // Try to verify payment status in case it was actually successful
      if (checkoutId) {
        await this.verifyPaymentStatus(checkoutId);
      } else {
        // Try payment recovery similar to success component
        await this.attemptPaymentRecovery(params);
      }
    });
  }

  private extractCheckoutId(params: any): string | null {
    // Try multiple parameter names that Maya might use
    const possibleIds = [
      'checkoutId', 'id', 'checkout_id', 'transactionId', 
      'transaction_id', 'payment_id', 'session_id'
    ];
    
    for (const key of possibleIds) {
      if (params[key]) {
        return params[key];
      }
    }
    
    return null;
  }

  private async attemptPaymentRecovery(params: any): Promise<void> {
    this.isVerifying = true;
    
    try {
      // Try to find recent payment from current user's session
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user && user.uid) {
        
        // Check for recent online payments for this user
        const recentPayment = await this.findRecentUserPayment(user.uid);
        if (recentPayment) {
          
          this.transactionDetails.checkoutId = recentPayment.checkout_id;
          this.transactionDetails.reference = recentPayment.transaction_reference || recentPayment.id;
          this.transactionDetails.amount = recentPayment.amount;
          
          // Handle bulk vs single payment
          if (recentPayment.isBulkPayment) {
            this.transactionDetails.isBulkPayment = true;
            this.transactionDetails.sessionCount = recentPayment.sessionCount;
            this.transactionDetails.plateNumbers = recentPayment.plateNumbers;
            this.transactionDetails.zoneNames = recentPayment.zoneNames;
            // For display purposes, show summary
            this.transactionDetails.plateNumber = recentPayment.plateNumbers.length > 1 ? 
              `${recentPayment.plateNumbers.length} vehicles` : 
              (recentPayment.plateNumbers[0] || 'Multiple');
            this.transactionDetails.zoneName = recentPayment.zoneNames.length > 1 ? 
              'Multiple zones' : 
              (recentPayment.zoneNames[0] || 'Multiple Zones');
          } else {
            this.transactionDetails.plateNumber = recentPayment.plate_number;
            this.transactionDetails.zoneName = recentPayment.zone_name;
          }
          
          // Try to verify this payment
          await this.verifyPaymentStatus(recentPayment.checkout_id);
          return;
        }
      }
      
      // If no recovery possible, proceed with failure
      this.showFailureState();
      
    } catch (error) {
      console.error('Payment recovery failed:', error);
      this.showFailureState();
    }
  }

  private async findRecentUserPayment(userId: string): Promise<any> {
    try {
      // Check localStorage for any stored payment info
      const storedPayment = localStorage.getItem('lastPaymentAttempt');
      if (storedPayment) {
        const paymentData = JSON.parse(storedPayment);
        // Check if it's from the last 10 minutes
        const paymentTime = new Date(paymentData.timestamp);
        const now = new Date();
        const diffMinutes = (now.getTime() - paymentTime.getTime()) / (1000 * 60);
        
        if (diffMinutes <= 10) {
          
          // Handle both bulk and single payments
          if (paymentData.session_count > 1 || paymentData.parking_session_ids) {
            paymentData.isBulkPayment = true;
            paymentData.sessionCount = paymentData.session_count || (paymentData.parking_session_ids ? paymentData.parking_session_ids.length : 1);
            paymentData.plateNumbers = paymentData.plate_numbers || [];
            paymentData.zoneNames = paymentData.zone_names || [];
          }
          
          return paymentData;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error finding recent payment:', error);
      return null;
    }
  }

  private showFailureState(): void {
    this.isVerifying = false;
    this.startCountdown();
  }

  async verifyPaymentStatus(checkoutId: string) {
    try {
      this.isVerifying = true;
      
      // First, try to get payment details from online_payments collection
      const onlinePayment = await this.paymentService.getOnlinePaymentByCheckoutId(checkoutId);
      
      if (onlinePayment) {
        
        // Update transaction details
        this.transactionDetails.amount = onlinePayment.amount || this.transactionDetails.amount;
        this.transactionDetails.plateNumber = onlinePayment.plate_number;
        this.transactionDetails.zoneName = onlinePayment.zone_name;
        this.transactionDetails.reference = onlinePayment.transaction_reference || onlinePayment.id;
        
        // Check if actually marked as paid (redirect to success)
        if (onlinePayment.status === 'paid' || onlinePayment.payment_status === 'paid' || 
            onlinePayment.status === 'completed' || onlinePayment.payment_status === true) {
          this.router.navigate([this.routeHelper.getRoutes().paymentSuccess], {
            queryParams: {
              checkoutId: checkoutId,
              reference: this.transactionDetails.reference,
              amount: this.transactionDetails.amount
            }
          });
          return;
        }
      }
      
      // Try to verify via payment service
      const paymentResult = await this.paymentService.verifyPaymentStatus(checkoutId);
      
      if (paymentResult && paymentResult.success && 
          (paymentResult.status === 'paid' || paymentResult.payment?.status === 'paid')) {
        // Payment was actually successful, redirect to success page
        this.router.navigate([this.routeHelper.getRoutes().paymentSuccess], {
          queryParams: {
            checkoutId: checkoutId,
            reference: this.transactionDetails.reference,
            amount: paymentResult.payment?.amount || this.transactionDetails.amount
          }
        });
        return;
      }

      // Payment is indeed failed
      this.showFailureState();

    } catch (error) {
      console.error('Payment verification error:', error);
      this.verificationError = 'Could not verify payment status. Please contact support if you believe this payment should have succeeded.';
      this.showFailureState();
    }
  }

  private startCountdown() {
    // Clear any existing intervals
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown === 0) {
        clearInterval(this.countdownInterval);
        this.goToDashboard();
      }
    }, 1000);
  }

  goToDashboard() {
    // Clear any stored payment attempt data
    localStorage.removeItem('lastPaymentAttempt');
    
    // Clear countdown interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    this.router.navigate([this.routeHelper.getRoutes().dashboard]);
  }

  tryAgain() {
    // Clear any stored payment attempt data
    localStorage.removeItem('lastPaymentAttempt');
    
    // Clear countdown interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    this.router.navigate([this.routeHelper.getRoutes().dashboard], { queryParams: { showPayment: true } });
  }

  contactSupport() {
    // Include transaction details in support email
    const emailBody = `I encountered an issue with my payment. Please assist me.\n\nTransaction Details:\n` +
      `- Reference: ${this.transactionDetails?.reference}\n` +
      `- Checkout ID: ${this.transactionDetails?.checkoutId}\n` +
      `- Amount: ${this.transactionDetails?.amount}\n` +
      `- Date: ${this.formatDate(this.transactionDetails?.date)}\n` +
      `- Error: ${this.transactionDetails?.error}\n` +
      `- Plate Number: ${this.transactionDetails?.plateNumber || 'N/A'}\n` +
      `- Zone: ${this.transactionDetails?.zoneName || 'N/A'}`;
      
    window.open(`mailto:support@parkbot.ph?subject=Payment Issue - ${this.transactionDetails?.reference}&body=${encodeURIComponent(emailBody)}`);
  }

  ngOnDestroy() {
    // Clean up interval on component destroy
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  formatDate(date: any): string {
    if (!date) return 'N/A';
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  }
}
